import { useEffect, useMemo, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Empty,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  alertSubscriptionRouterDeleteAlertSubscriptionV1AlertSubscriptionsIdDeleteMutation,
  alertSubscriptionRouterListAlertSubscriptionsV1AlertSubscriptionsGetOptions,
  alertSubscriptionRouterListAlertSubscriptionsV1AlertSubscriptionsGetQueryKey,
  listSitesInfiniteOptions,
} from "../../services/client/@tanstack/react-query.gen";
import { AlertSubscription } from "../../services/client/types.gen";
import { useNotify } from "../../components/util-components/Notifications/notifications";
import { DisableForViewer } from "../../components/common/DisableForViewer";
import { AlertSubscriptionModal } from "./AlertSubscriptionModal";
import { EVENT_FILTER_LABELS, SITE_FILTER_LABELS } from "./constants";
import { extractErrorMessage } from "./utils";

// The backend caps page_size at 100, so we page through all sites for the filter UI.
const SITE_FETCH_PAGE_SIZE = 100;

export const Alerting = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AlertSubscription | null>(null);

  const {
    data: subscriptions,
    isLoading,
    error,
  } = useQuery(
    alertSubscriptionRouterListAlertSubscriptionsV1AlertSubscriptionsGetOptions(),
  );

  const {
    data: sitesData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...listSitesInfiniteOptions({
      query: { page_size: SITE_FETCH_PAGE_SIZE },
    }),
    getNextPageParam: (lastPage) => lastPage.end_cursor,
  });

  // Fetch every page so the "specific sites" selector lists all of the partner's sites.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const sites = useMemo(
    () => sitesData?.pages.flatMap((page) => page.data) ?? [],
    [sitesData],
  );
  const siteNameById = useMemo(
    () => new Map(sites.map((site) => [site.site_id, site.name])),
    [sites],
  );

  const deleteMutation = useMutation({
    ...alertSubscriptionRouterDeleteAlertSubscriptionV1AlertSubscriptionsIdDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey:
          alertSubscriptionRouterListAlertSubscriptionsV1AlertSubscriptionsGetQueryKey(),
      });
      notify({ type: "success", message: "Alert subscription deleted" });
    },
    onError: (err) => {
      notify({
        type: "error",
        message: "Failed to delete alert subscription",
        description: extractErrorMessage(err),
      });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (subscription: AlertSubscription) => {
    setEditing(subscription);
    setModalOpen(true);
  };

  const renderSiteFilter = (subscription: AlertSubscription) => {
    if (subscription.site_filter.type === "ALL_SITES") {
      return <Tag color="blue">{SITE_FILTER_LABELS.ALL_SITES}</Tag>;
    }
    const siteIds = subscription.site_filter.site_ids ?? [];
    return (
      <Space size={[4, 4]} wrap>
        {siteIds.map((siteId) => (
          <Tag key={siteId}>{siteNameById.get(siteId) ?? siteId}</Tag>
        ))}
      </Space>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-2">
        <Typography.Title level={2} className="!mb-0">
          Alerting
        </Typography.Title>
        <DisableForViewer>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New subscription
          </Button>
        </DisableForViewer>
      </div>

      <Typography.Paragraph type="secondary">
        Get notified by email when a site runs into a validation error.
      </Typography.Paragraph>

      {!!error && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Failed to load alert subscriptions"
          description={extractErrorMessage(error)}
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : !subscriptions || subscriptions.length === 0 ? (
        <Empty description="No alert subscriptions yet">
          <DisableForViewer>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Create your first subscription
            </Button>
          </DisableForViewer>
        </Empty>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {subscriptions.map((subscription) => (
            <Card
              key={subscription._id}
              size="small"
              title={
                <Space>
                  <MailOutlined />
                  {subscription.channels.length === 1
                    ? subscription.channels[0].email
                    : `${subscription.channels.length} recipients`}
                </Space>
              }
              extra={
                <Space>
                  <DisableForViewer>
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      title="Edit"
                      onClick={() => openEdit(subscription)}
                    />
                  </DisableForViewer>
                  <Popconfirm
                    title="Delete this subscription?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() =>
                      subscription._id &&
                      deleteMutation.mutate({
                        path: { id: subscription._id },
                      })
                    }
                  >
                    <DisableForViewer>
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        title="Delete"
                      />
                    </DisableForViewer>
                  </Popconfirm>
                </Space>
              }
            >
              {subscription.channels.length > 1 && (
                <div className="mb-2">
                  <Typography.Text type="secondary">Recipients</Typography.Text>
                  <div>
                    <Space size={[4, 4]} wrap>
                      {subscription.channels.map((channel) => (
                        <Tag key={channel.email}>{channel.email}</Tag>
                      ))}
                    </Space>
                  </div>
                </div>
              )}

              <div className="mb-2">
                <Typography.Text type="secondary">Alerts on</Typography.Text>
                <div>
                  <Space size={[4, 4]} wrap>
                    {subscription.event_filter.map((event) => (
                      <Tag key={event} color="orange">
                        {EVENT_FILTER_LABELS[event]}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </div>

              <div>
                <Typography.Text type="secondary">Applies to</Typography.Text>
                <div>{renderSiteFilter(subscription)}</div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertSubscriptionModal
        open={modalOpen}
        subscription={editing}
        sites={sites}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};
